export const RISK_LEVELS = {
  LOW: 'Green',
  MODERATE: 'Yellow',
  HIGH: 'Red',
  NA: 'NA',
  IN_PLANNING: 'In Planning'
} as const;

/** Stored risk values (same strings as RISK_LEVELS) → human-readable labels (Low, Moderate, High). */
export const RISK_LEVEL_AUDIT_LABEL: Record<
  (typeof RISK_LEVELS)[keyof typeof RISK_LEVELS],
  string
> = {
  [RISK_LEVELS.LOW]: 'Low',
  [RISK_LEVELS.MODERATE]: 'Moderate',
  [RISK_LEVELS.HIGH]: 'High',
  [RISK_LEVELS.IN_PLANNING]: 'In Planning',
  [RISK_LEVELS.NA]: 'NA'
};

function resolveRiskLevelLabel(value: unknown): string | undefined {
  const s = value == null ? '' : String(value).trim();
  if (!s) return undefined;
  if (s in RISK_LEVEL_AUDIT_LABEL) {
    return RISK_LEVEL_AUDIT_LABEL[s as keyof typeof RISK_LEVEL_AUDIT_LABEL];
  }
  const lower = s.toLowerCase();
  for (const [stored, label] of Object.entries(RISK_LEVEL_AUDIT_LABEL)) {
    if (stored.toLowerCase() === lower) return label;
  }
  // UI / shorthand aliases (slideout features use low/moderate/high)
  if (lower.includes('planning') || lower === 'in_planning') {
    return RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.IN_PLANNING];
  }
  if (lower.includes('high') || lower === 'red') {
    return RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.HIGH];
  }
  if (lower.includes('moderate') || lower === 'yellow') {
    return RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.MODERATE];
  }
  if (lower.includes('low') || lower === 'green') {
    return RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.LOW];
  }
  if (lower === 'na' || lower === 'n/a') {
    return RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.NA];
  }
  return undefined;
}

type StoredRiskLevel = (typeof RISK_LEVELS)[keyof typeof RISK_LEVELS];

/**
 * Maps API/UI aliases (Low, High, green, red, etc.) to canonical stored values
 * (Green, Yellow, Red) used by timeline cards and icon rendering.
 */
export function normalizeRiskLevelToStored(
  value: unknown
): StoredRiskLevel | undefined {
  const s = value == null ? '' : String(value).trim();
  if (!s) return undefined;

  for (const stored of Object.values(RISK_LEVELS)) {
    if (stored.toLowerCase() === s.toLowerCase()) {
      return stored;
    }
  }

  const lower = s.toLowerCase();
  if (lower.includes('planning') || lower === 'in_planning') {
    return RISK_LEVELS.IN_PLANNING;
  }
  if (lower.includes('high') || lower === 'red') {
    return RISK_LEVELS.HIGH;
  }
  if (lower.includes('moderate') || lower === 'yellow') {
    return RISK_LEVELS.MODERATE;
  }
  if (lower.includes('low') || lower === 'green') {
    return RISK_LEVELS.LOW;
  }
  if (lower === 'na' || lower === 'n/a') {
    return RISK_LEVELS.NA;
  }
  return undefined;
}

/** Maps stored/UI risk values to labels; preserves empty and unrecognized values as-is. */
export function riskLevelToLabel(value: unknown): string {
  const s = value == null ? '' : String(value).trim();
  if (!s) return '';
  return resolveRiskLevelLabel(value) ?? s;
}

/** API payload mapping: defaults empty/unrecognized values to Low (feature create). */
export function riskLevelToApiLabel(value: unknown): string {
  return (
    resolveRiskLevelLabel(value) ?? RISK_LEVEL_AUDIT_LABEL[RISK_LEVELS.LOW]
  );
}

export const STATUS_VALUES = {
  ACTIVE: 'ACTIVE', //To Do
  COMPLETE: 'COMPLETED', //Done
  CLOSED: 'CLOSED'
} as const;

/** Canonical string shared by {@link MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS} and legacy {@link MOMENT_TYPES.OFFER_PRICE_PROGRAM}. */
const MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS =
  'Price Changes and Offers' as const;

export const MOMENT_TYPES = {
  PRODUCT_RELEASE: 'Product Release without Marketing',
  MARKETING_MOMENT_AND_PRODUCT_RELEASE: 'Product Release with Marketing',
  MARKETING_MOMENT: 'Marketing Moment',
  FEATURE: 'Feature',
  KEY_DATE: 'Key Dates',
  EVENT: 'Events',
  /** @deprecated Prefer {@link MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS}; same string for backward compatibility. */
  OFFER_PRICE_PROGRAM: MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS,
  PROMOS_OFFERS: 'Offer, Price Change, or Program',
  PRICE_CHANGES_AND_OFFERS: MOMENT_TYPE_PRICE_CHANGES_AND_OFFERS,
  NEW_1_0_RELEASE: 'New 1.0 Release',
  END_OF_LIFE: 'End of Life (EOL)'
} as const;

/** Grey prepend label for EOL name fields (Create RSD form, quick edit). */
export const EOL_NAME_PREPEND_LABEL = 'EOL:';

/** Roadmap / timeline cards for End of Life moments show a gray `EOL:` title prefix. */
export function isEolMoment(momentType?: string | null): boolean {
  return momentType === MOMENT_TYPES.END_OF_LIFE;
}

/**
 * New PCO moments keep `momentType` "Offer, Price Change, or Program" but set `pco: true`
 * so they can share date/GenAI rules with "Price Changes and Offers" without a distinct type string.
 */
export function isPcoMoment(pco?: boolean | null): boolean {
  return pco === true;
}

/**
 * Timeline / tooltip date slots that follow customer-communication + effective-date
 * (same as "Price Changes and Offers"), including PCO-flagged legacy-type moments.
 */
export function isPriceChangeStyleOfferMoment(
  projectType: string | undefined,
  pco?: boolean | null
): boolean {
  return (
    projectType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS ||
    (projectType === MOMENT_TYPES.PROMOS_OFFERS && isPcoMoment(pco))
  );
}

/**
 * GenAI (and consumes-credit tied to it) apply to legacy "Offer, Price Change, or Program"
 * and other types — not to "Price Changes and Offers" or PCO-flagged offer moments (PAR path).
 */
export function momentTypeSupportsGenAi(
  momentType: string | undefined | null,
  ctx?: { pco?: boolean | null }
): boolean {
  if (momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS) {
    return false;
  }
  if (isPcoMoment(ctx?.pco)) {
    return false;
  }
  return true;
}

/** Backend string values for milestone type-of-review used across the edit milestone modal. */
export const MILESTONE_REVIEW_TYPE = {
  TBD: 'TBD',
  MILESTONE_NOT_REQUIRED: 'Milestone Not Required',
  MEETING: 'Meeting',
  ASYNC: 'Async'
} as const;

export type MilestoneReviewTypeTbdOrNotRequired =
  (typeof MILESTONE_REVIEW_TYPE)[keyof typeof MILESTONE_REVIEW_TYPE];

export const RSD_CREATE_IAM_GROUP = 'GRP-ADOBE-ROADMAP-CREATE-PROD';

export const JIRA_CORP_LINK = ['jira.corp.adobe.com'];
export const JIRA_LINK = [/^ADR-\d+$/];
export const JIRA_ISSUE_ID = { 'jira.corp.adobe.com': /^\/browse\/ADR-\d+$/i };
export const JIRA_ERROR_MESSAGE =
  'Please enter a valid JIRA URL (e.g., https://jira.corp.adobe.com/browse/ADR-123) or ADR-123';

export const SEARCH_OUTSIDE_MUST_WINS_MSG =
  'Your search result is outside the current view. Do you wish to continue to the default Roadmap layout to view your search result?';

export const SEND_TARGET_DEFAULT_DATE_HELPER_MESSAGE =
  'Changes are permanent. After updating the guidance, you will own all future modifications.';

export const SHOW_ON_THE_ROADMAP_TOOLTIP_MESSAGE =
  'When the Release date is different from the Marketing Moment Date, two separate cards will be shown automatically. Deselecting the toggle will remove the release date card from the roadmap.';
export const INHERIT_MILESTONE_TOOLTIP_MESSAGE =
  'If your release is part of a parent/umbrella program and the SLC reviews and artifacts are tracked there, you can inherit the milestones from the parent program. These will be displayed as read-only within this RSD for clarity.';
export const PARENT_MOMENT_TOOLTIP_MESSAGE =
  'Use this field to associate your release with the parent/umbrella program, such as MAX, Summit, etc.';
export const HEADLINE_CRITICAL_UPDATE_TOOLTIP_MESSAGE =
  'Holistic overview of critical updates to the release/moment; key changes in the RSD, plan for getting to a plan, state of the release/moment, critical risks, and path to green.  Focus on updates from the last week or recent updates that are important to ensure people are aware of. Also helpful to highlight recent updates in the content below. Must be REFRESHED WEEKLY at a minimum.';
export const SLIDEOUT_PRODUCT_ZEN_TOOLTIP_MESSAGE =
  'A short, customer-centric description of the ideal outcome a product or portfolio is meant to create. E.g. "Help X audience do Y without Z," or "Nurture creativity to win the hearts and minds of [segment]."';
export const SLIDEOUT_WHAT_TOOLTIP_MESSAGE =
  'Provide a concise 1-2 sentence description of the product release or marketing moment. If a marketing-only moment, what is being announced or shared, and what is the theme of the moment?';
export const SLIDEOUT_WHO_TOOLTIP_MESSAGE =
  'This should include a mix of creator segments, plans or entitlements, product usage, or customer states. If necessary, include any specific micro-audiences (e.g., documentary filmmakers)';
export const SLIDEOUT_HOW_TOOLTIP_MESSAGE =
  'How is the new product value different from other solutions in the market? List specific Adobe advantages for this release.';
export const SLIDEOUT_WHERE_TOOLTIP_MESSAGE =
  'What regions will this launch? Will this be a Global launch? If event specific, what regions will be represented at the event?';
export const SLIDEOUT_STRETEGIC_ALIGNMENT_TOOLTIP_MESSAGE =
  'Which strategic pillar this aligns with or supports.';
export const SLIDEOUT_MVP_STATUS_TOOLTIP_MESSAGE = `Reflects only features that are truly committed (all dependencies and resources) and would delay release if not ready. Teams can continue to add to the MVP as features become committed. This keeps Leadership aligned on what's really in and gives Marketing clarity on what can be included in messaging.`;

export const SLIDEOUT_TOOLTIP_MAPPING = {
  'HEADLINE / CRITICAL UPDATE': HEADLINE_CRITICAL_UPDATE_TOOLTIP_MESSAGE,
  'PRODUCT ZEN': SLIDEOUT_PRODUCT_ZEN_TOOLTIP_MESSAGE,
  'WHAT ARE WE LAUNCHING?': SLIDEOUT_WHAT_TOOLTIP_MESSAGE,
  'WHO IS IT FOR?': SLIDEOUT_WHO_TOOLTIP_MESSAGE,
  'HOW IS IT DIFFERENT?': SLIDEOUT_HOW_TOOLTIP_MESSAGE,
  'WHERE WILL IT LAUNCH?': SLIDEOUT_WHERE_TOOLTIP_MESSAGE,
  'STRATEGIC ALIGNMENT': SLIDEOUT_STRETEGIC_ALIGNMENT_TOOLTIP_MESSAGE,
  'MVP STATUS': SLIDEOUT_MVP_STATUS_TOOLTIP_MESSAGE
};

export const PAR_ID_MAX_LENGTH = 20;

/** Save-and-Send toast copy — Async milestone PATCH confirms whether the approval email actually went out. */
export const MILESTONE_EMAIL_SENT_SUCCESS_MESSAGE =
  'Email sent for Approval successfully';
export const MILESTONE_EMAIL_NOT_SENT_ERROR_MESSAGE =
  'Email sent failed, Please resend';

export const MOMENT_TYPE_CHANGE_MESSAGES = {
  REMOVE_MARKETING_DATE: {
    title: 'Please confirm you want to proceed with this change?',
    message:
      'Note: This action will remove the Marketing Moment date and cannot be undone.'
  },
  AUTO_POPULATE_MARKETING_DATE: {
    title: 'Please confirm you want to proceed with this change?',
    message:
      'Note: Marketing Moment date will be auto-populated with the Release Date to ensure this record appears on the roadmap.\nYou can edit the dates to be different, if needed, once the record is updated.'
  }
};
